import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
    bloodPressureDataRange,
    colHeaderBackground,
    colHeaderColor,
    consciousnessOptions,
    dataCols,
    headerData,
    holderValue,
    obsColWrapperData,
    observationKeys,
    pulseDataRange,
    respirationDataRange,
    setLineChartDrawn,
    shortenValueKeys,
    spO2DataRange,
    temperatureDataRange,
} from './Constants'

import {obsEvent, removeOnMenuChanged} from './ObservationService'

//Prime React
import {Button} from 'primereact/button'
import {Column} from 'primereact/column'
import {DataTable} from 'primereact/datatable'

import {ReactComponent as BsRobot} from '../../assets/icons/BsRobot.svg'

import moment from 'moment'
import {useHistory, useParams} from 'react-router-dom'
import {useLocation} from 'react-router-dom/cjs/react-router-dom'
import {useDebouncedCallback} from 'use-debounce'
import {useScreenshot} from 'use-react-screenshot'
import {v4 as uuidv4} from 'uuid'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {useAuth} from '../authentication/AuthProvider'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {useParameterByUser} from '../parameter/query-keys/ParameterKeysManager'
import {useDetailPatient} from '../patient/query-keys/PatientKeysManager'
import {permissionOptionKeys} from '../role/RoleConstant'
import {useRoleContext} from '../role/RoleProvider'
import {usePatientContext} from '../styled-component/information/StyleDetailInformationWrapper'
import StyledNoteSideBar from '../styled-component/note/StyledNoteSideBar'
import StyledParameterSideBar from '../styled-component/parameter/StyledParameterSideBar'
import StyledSymptomSideBar from '../styled-component/symptom/StyledSymptomSideBar'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import LineCanvas from './LineCanvas'
import ObservationComment from './ObservationComment'
import ObservationConfig from './ObservationConfig'
import ObservationsForm from './ObservationForm'
import ObservationPdfPreview from './ObservationPdfPreview'
import {ObservationSummary} from './ObservationSummary'
import {ReportButton} from './ReportButton'
import {
    convertSkinTempToMBT,
    getIndexOfValueInMultiCol,
    getNewsColor,
    getShortenFromValue,
    getValueByKeyAtPosition,
    getValueByKeyForChart,
    isArrayAndNotEmpty,
    setIncrement,
} from './Utils'
import {usePageHealthRecords} from './query-keys/ObsevationKeysManager'

export const PATH = 'healthRecord'
export const QueryTableKey = 'Health-Record'

const initialPageable = {
    rows: 24,
    pageNumber: 0,
    sortField: 'createdAt',
    sortOrder: -1,
}

const Observations = () => {
    //#region Context
    const {user} = useAuth()
    const {myRolePermissions} = useRoleContext()
    const {userRole, timezoneId} = useAppConfig()
    const {handleCheckPermissionByKey} = usePatientContext()
    //#endregion

    //#region Translate
    const {translateKeys, translateObservations, translateCommons} =
        TranslateUtils()
    //#endregion

    //#region Ref
    const obsSummary = useRef(null)
    const chartWrapper = useRef(null)
    const obsForm = useRef(null)
    const obsConfig = useRef(null)
    const obsComment = useRef(null)
    const obsPdf = useRef(null)
    const noteSideBar = useRef(null)
    const symptomSideBar = useRef(null)
    const parameterSideBar = useRef(null)
    const screenshotRef = useRef(null)
    const canvasLineRef = useRef([])
    const lineRef = useRef([])
    //#endregion

    //#region Import
    const history = useHistory()
    const [image, takeScreenshot] = useScreenshot()
    //#endregion

    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        return !!idString ? parseInt(idString) : null
    }, [idString])
    //#endregion

    //#region State
    const [pageable, setPageable] = useState({...initialPageable})
    // eslint-disable-next-line no-unused-vars
    const [showColors, setShowColors] = useState(true)

    const InitialHealthRecordsFilterRequest = useMemo(
        () => ({userId: id}),
        [id],
    )
    //#endregion

    //#region Query
    const {data: userData} = useDetailPatient(user?.id, id, !!user?.id && !!id)

    const {
        data: obsData,
        isLoading: isLoadingObs,
        refetch: refetchObs,
    } = usePageHealthRecords(
        user?.id,
        pageable.pageNumber,
        pageable.rows,
        pageable.sortField,
        pageable.sortOrder,
        InitialHealthRecordsFilterRequest,
        !!user?.id && !!id,
    )

    const {data: parameterData} = useParameterByUser(
        user?.id,
        id,
        !!user?.id && !!id,
    )
    //#endregion

    //#region Handle PDF
    const [isShowCanvasLine, setIsShowCanvasLine] = useState(false)

    const handleTakeScreenshot = useDebouncedCallback(async () => {
        takeScreenshot(screenshotRef.current).then(image => {
            obsPdf.current.image(image)
            setIsShowCanvasLine(false)
        })
    }, 50)

    const handlePrintPdf = useCallback(() => {
        obsPdf.current.showForm()
        showNotification('info', "Please wait while we're preparing the PDF.")
        setIsShowCanvasLine(true)
    }, [])

    useEffect(() => {
        if (!isShowCanvasLine) {
            return
        }
        handleTakeScreenshot()
    }, [handleTakeScreenshot, isShowCanvasLine])
    //#endregion

    //#region Memo
    const data = useMemo(() => {
        if (obsData?.content) {
            return obsData.content
                ?.sort((a, b) =>
                    b.createdAt && a.createdAt
                        ? moment(b.createdAt).toDate() -
                          moment(a.createdAt).toDate()
                        : 0,
                )
                ?.reverse()
        }
        return []
    }, [obsData])
    //#endregion

    //#region Render OBS TABLE
    //#region BODY COLUMNS
    const convertMultipleColData = useCallback(
        (key, data, idx, isShorten = false) => {
            let returnValue = ''
            // eslint-disable-next-line default-case
            switch (key) {
                case observationKeys.respiration:
                    returnValue = getValueByKeyAtPosition(
                        respirationDataRange,
                        key,
                        data,
                        idx,
                    )
                    break
                case observationKeys.oxygen:
                    returnValue = getValueByKeyAtPosition(
                        spO2DataRange,
                        key,
                        data,
                        idx,
                    )
                    break
                case observationKeys.heartRate:
                    returnValue = getValueByKeyAtPosition(
                        pulseDataRange,
                        key,
                        data,
                        idx,
                    )
                    break
                case observationKeys.bloodPressure:
                    returnValue = getValueByKeyAtPosition(
                        bloodPressureDataRange,
                        observationKeys.diastolic,
                        data,
                        idx,
                    )
                    if (!returnValue) {
                        returnValue = getValueByKeyAtPosition(
                            bloodPressureDataRange,
                            observationKeys.systolic,
                            data,
                            idx,
                        )
                    }
                    break
                case observationKeys.levelOfConsciousness:
                    if (idx === 0 && data[key] && data[key] === 'alert') {
                        returnValue = isShorten
                            ? getShortenFromValue(
                                  data[key],
                                  consciousnessOptions,
                              )
                            : ''
                    } else {
                        if (idx === 0) {
                            returnValue = ''
                        } else {
                            returnValue =
                                data[key] && data[key] !== 'alert' && isShorten
                                    ? getShortenFromValue(
                                          data[key],
                                          consciousnessOptions,
                                      )
                                    : ''
                        }
                    }

                    if (idx === 1 && data[key] && data[key] === 'confusion') {
                        returnValue += data['confusion'] ? '(Y)' : '(N)'
                    }
                    break
                case observationKeys.temperature:
                    returnValue = getValueByKeyAtPosition(
                        temperatureDataRange,
                        key,
                        data,
                        idx,
                    )

                    break
            }

            if (showColors && returnValue === '') {
                return holderValue
            } else {
                return returnValue
            }
        },
        [showColors],
    )

    const renderColData = useCallback(
        (rowData, data, i) => (
            <div>
                {rowData.colNumber > 1 &&
                rowData.extraColData &&
                rowData.extraColData.length > 0 ? (
                    <div
                        className="col-12 p-0 m-0 flex flex-column align-items-center justify-content-between"
                        style={{height: rowData.extraColData.length * 25}}>
                        {rowData.extraColData.map((row, idx) => {
                            const dataValue = convertMultipleColData(
                                rowData.key,
                                data,
                                idx,
                                true,
                            )

                            return (
                                <div
                                    key={`${rowData.key}-${row}-colData-${idx}`}
                                    className={`font-bold ${
                                        dataValue && dataValue !== holderValue
                                            ? 'px-2'
                                            : 'px-3'
                                    } tooltip px-0 text-center`}
                                    style={{
                                        backgroundColor: rowData.colors
                                            ? rowData.colors[idx]
                                            : '',
                                        cursor: 'pointer',
                                        height: '24px',
                                        width: '100%',
                                        marginBottom: '0.5px',
                                        paddingTop: '2px',
                                        border: '0.1px solid rgba(204, 204, 204, 0.1)',
                                    }}>
                                    {dataValue && dataValue !== holderValue && (
                                        <span className="tooltiptext-top">
                                            {rowData.label ? (
                                                <div>
                                                    {translateKeys(
                                                        rowData.label,
                                                    )}
                                                </div>
                                            ) : (
                                                ''
                                            )}
                                            {rowData.extraColData[idx] ? (
                                                <div>
                                                    <div>
                                                        {
                                                            rowData
                                                                .extraColData[
                                                                idx
                                                            ]
                                                        }
                                                    </div>{' '}
                                                    {shortenValueKeys.includes(
                                                        rowData.key,
                                                    ) ? (
                                                        <div>
                                                            {convertMultipleColData(
                                                                rowData.key,
                                                                data,
                                                                idx,
                                                                false,
                                                            )}
                                                        </div>
                                                    ) : (
                                                        ''
                                                    )}
                                                </div>
                                            ) : (
                                                ''
                                            )}
                                        </span>
                                    )}
                                    <span
                                        id={`${rowData.key}-${i}-${idx}`}
                                        className="px-1">
                                        {dataValue || ''}
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div
                        className="flex justify-content-center align-items-center m-0 p-0"
                        style={{
                            height: '24px',
                            backgroundColor:
                                rowData.key === observationKeys.news
                                    ? getNewsColor(data[rowData.key])
                                    : '',
                        }}>
                        <div className={`text-center font-bold`}>
                            {rowData.key === observationKeys.year ||
                            rowData.key === observationKeys.date ||
                            rowData.key === observationKeys.time ? (
                                <p>{data[rowData.key]}</p>
                            ) : (
                                <React.Fragment>
                                    <div className="tooltip mb-0">
                                        {rowData.key !==
                                            observationKeys.comment && (
                                            <span className={'tooltiptext-top'}>
                                                <div className="text-center">
                                                    <p className="text-center">
                                                        {translateKeys(
                                                            convertKeyToLabel(
                                                                rowData.key,
                                                            ),
                                                        )}
                                                        {': '}
                                                        {data[rowData.key]}
                                                    </p>
                                                </div>
                                            </span>
                                        )}

                                        {rowData.key ===
                                        observationKeys.comment ? (
                                            !!data[rowData.key] ? (
                                                <i
                                                    className="pi pi-bookmark pointer"
                                                    onClick={() =>
                                                        obsComment.current.showForm(
                                                            {
                                                                comment:
                                                                    data?.comment ??
                                                                    '',
                                                                initials:
                                                                    data?.initials ??
                                                                    '',

                                                                fullName:
                                                                    userData?.fullName
                                                                        ? userData?.fullName
                                                                        : '',

                                                                dateTime:
                                                                    data?.date &&
                                                                    data?.year &&
                                                                    data?.time
                                                                        ? `${data?.date}-${data?.year}${data?.time}`
                                                                        : '-',
                                                            },
                                                        )
                                                    }></i>
                                            ) : null
                                        ) : rowData.key ===
                                              observationKeys.initials &&
                                          data['initialsRedirectUrl'] ? (
                                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                            <a
                                                className="pointer"
                                                onClick={e => {
                                                    e.preventDefault()

                                                    // eslint-disable-next-line no-unused-expressions
                                                    userRole ===
                                                        SystemRole.HCP &&
                                                        (data[
                                                            'initialsRedirectUrl'
                                                        ].includes('hcp')
                                                            ? myRolePermissions?.permissions?.find(
                                                                  item =>
                                                                      item?.roleCategory ===
                                                                      'hcpManagement',
                                                              )
                                                                  .allowToViewHcpInfo
                                                                ? history.push(
                                                                      data[
                                                                          'initialsRedirectUrl'
                                                                      ],
                                                                  )
                                                                : showNotification(
                                                                      severityNotification.info,
                                                                      "You don't have sufficient permissions to access HCP Information",
                                                                  )
                                                            : data[
                                                                  'initialsRedirectUrl'
                                                              ].includes(
                                                                  'patients',
                                                              )
                                                            ? myRolePermissions?.permissions?.find(
                                                                  item =>
                                                                      item?.roleCategory ===
                                                                      'patientManagement',
                                                              )
                                                                  .allowToViewPatientInfo
                                                                ? history.push(
                                                                      data[
                                                                          'initialsRedirectUrl'
                                                                      ],
                                                                  )
                                                                : showNotification(
                                                                      severityNotification.info,
                                                                      "You don't have sufficient permissions to access Patient Information",
                                                                  )
                                                            : '')
                                                }}>
                                                {data[rowData.key]}
                                            </a>
                                        ) : rowData.key ===
                                          observationKeys.anySupplementaryOxygen ? (
                                            <p>
                                                {data[rowData.key]
                                                    ? convertKeyToLabel(
                                                          data[rowData.key],
                                                      )
                                                    : ''}
                                            </p>
                                        ) : (
                                            <p>{data[rowData.key]}</p>
                                        )}
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                )}
            </div>
        ),
        [
            history,
            userRole,
            translateKeys,
            userData?.fullName,
            myRolePermissions?.permissions,
            convertMultipleColData,
        ],
    )

    const renderEmptyColData = rowData => (
        <div>
            {rowData.colNumber > 1 &&
            rowData.extraColData &&
            rowData.extraColData.length > 0 ? (
                <div
                    className="col-12 p-0 flex flex-column align-items-center justify-content-between"
                    style={{height: rowData.extraColData.length * 25}}>
                    {rowData.extraColData.map((row, idx) => {
                        return (
                            <div
                                key={`${uuidv4()}`}
                                className={`font-bold px-3 tooltip px-0 text-center`}
                                style={{
                                    backgroundColor: rowData.colors
                                        ? rowData.colors[idx]
                                        : '',
                                    cursor: 'pointer',
                                    height: '24px',
                                    width: '100%',
                                    marginBottom: '0.5px',
                                    paddingTop: '2px',
                                    border: '0.1px solid rgba(204, 204, 204, 0.1)',
                                }}>
                                <span>{` `}</span>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <div
                    className={`${
                        rowData.key === 'Comment' ? 'tooltip' : ''
                    } text-center`}>
                    {` `}
                </div>
            )}
        </div>
    )

    const renderColBody = useCallback(() => {
        let cols = []
        for (let i = 0; i < dataCols; i++) {
            cols.push(
                <Column
                    key={`${data[i]}-${i}`}
                    style={{
                        width: '3%',
                    }}
                    body={rowData => {
                        return data[i]
                            ? renderColData(rowData, data[i], i)
                            : renderEmptyColData(rowData)
                    }}
                />,
            )
        }

        setIncrement()

        return cols
    }, [data, renderColData])
    //#endregion

    //#region COL HEADER
    const renderColWrapper = useCallback(
        (rowData, type) => {
            return (
                <div
                    style={{
                        backgroundColor: colHeaderBackground,
                        minWidth: '120px',
                    }}>
                    {rowData.colNumber > 1 ? (
                        <div className="grid p-0">
                            <div className="col-6 p-0 flex justify-content-center align-items-center font-bold p-0">
                                <div
                                    className="text-center p-0"
                                    style={
                                        rowData.key !==
                                        observationKeys.levelOfConsciousness
                                            ? {
                                                  transform: 'rotate(270deg)',
                                                  color: colHeaderColor,
                                              }
                                            : {color: colHeaderColor}
                                    }>
                                    {translateObservations(rowData.label)}
                                </div>
                            </div>
                            <div className="col-6 p-0">
                                <div
                                    className="p-0 font-bold"
                                    style={{
                                        backgroundColor: 'white',
                                    }}>
                                    {rowData.extraColData.map(
                                        (extraData, eIdx) => (
                                            <div
                                                key={`${type}-${extraData}-${eIdx}`}
                                                className="text-center font-bold"
                                                style={{
                                                    padding: '0.3rem',
                                                }}>
                                                <label className="font-fold">
                                                    {translateObservations(
                                                        extraData,
                                                    )}
                                                </label>
                                            </div>
                                        ),
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div
                            className="flex justify-content-center align-items-center font-bold"
                            style={{
                                minWidth: 40,
                                padding:
                                    rowData?.key === observationKeys.news
                                        ? '3px'
                                        : 10,
                                color: colHeaderColor,
                                textAlign: 'center',
                            }}>
                            {translateObservations(rowData.label)}
                        </div>
                    )}
                </div>
            )
        },
        [translateObservations],
    )
    //#endregion

    //#region TABLE HEADER - FOOTER
    const renderRatingScale = () => (
        <div className={`flex p-0 align-items-center`}>
            {headerData.map((item, idx) => (
                <div
                    key={`${item.key}-${idx}`}
                    className="grid justify-content-center align-items-center p-0">
                    {item.key === observationKeys.ratingScale && (
                        // Scale Info
                        <div className="grid justify-content-center align-items-center">
                            {item.data.map((cItem, cIdx) => (
                                <div
                                    key={`${item.key}-${idx}-${cIdx}`}
                                    style={{
                                        border: '1px solid rgba(221, 221, 221, 0.35)',
                                        backgroundColor: cItem,
                                        textAlign: 'center',
                                        padding: '5px 10px',
                                        cursor: 'default',
                                        borderRadius: '1.5px',
                                    }}>
                                    {cIdx}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    )

    const renderPatientInfo = useCallback(() => {
        const patientInfo = [
            {
                label: observationKeys.hospitalName,
                value: userData?.hospitalName,
            },
            {
                label: observationKeys.patientName,
                value: userData?.fullName,
            },
            {
                label: 'NHS Number',
                value: userData?.nhsNumber,
            },
            {
                label: observationKeys.age,
                value: userData?.age,
            },
            {
                label: observationKeys.arrivalDate,
                value: userData?.createdAt,
            },
        ]
        return (
            <div
                key={`${userData.id}`}
                className={`flex flex-wrap justify-content-around align-items-center gap-2 w-full`}>
                {patientInfo.map((item, idx) => (
                    <div key={idx} className="text-center p-2">
                        <h6 className="uppercase mb-2 pb-0">
                            {translateKeys(convertKeyToLabel(item.label))}
                        </h6>
                        <h5 className="font-semibold m-0 ">
                            {item.value ? item.value : '-'}
                        </h5>
                    </div>
                ))}
            </div>
        )
    }, [translateKeys, userData])

    const renderTableHeader = () => {
        return (
            <div className="grid col-12">
                <div className={`flex col-12 p-0 mb-3`}>
                    {
                        // Patient Info
                        userData && renderPatientInfo()
                    }
                </div>
                <div className="w-full flex flex-wrap justify-content-center mb-3">
                    <div className="flex flex-wrap justify-content-center">
                        {handleCheckPermissionByKey(
                            permissionOptionKeys.allowToAddObservations,
                        ) && (
                            <Button
                                className="p-button-sm p-button-info m-1 w-10rem"
                                label={translateKeys('ADD OBS')}
                                iconPos="left"
                                icon="pi pi-plus"
                                onClick={() => obsForm.current?.showForm()}
                            />
                        )}
                        <Button
                            className="p-button-info p-button-sm m-1 w-10rem"
                            icon="pi pi-copy"
                            label={translateKeys('NOTES')}
                            onClick={() => noteSideBar.current?.showForm()}
                        />

                        <Button
                            className="p-button-info p-button-sm m-1 w-10rem"
                            icon="pi pi-comments"
                            label={translateKeys('SYMPTOMS')}
                            onClick={() => symptomSideBar.current.showForm()}
                        />
                    </div>
                    <div className="flex flex-wrap justify-content-center">
                        <Button
                            className="p-button-info p-button-sm m-1 w-10rem"
                            icon="pi pi-sliders-h"
                            label={translateKeys('PARAMETER')}
                            onClick={() => parameterSideBar.current.showForm()}
                        />
                        <Button
                            className="p-button-info p-button-sm m-1 w-10rem"
                            label={translateKeys('DOWNLOAD AS PDF')}
                            iconPos="left"
                            icon={
                                isShowCanvasLine
                                    ? 'pi pi-spin pi-spinner'
                                    : 'pi pi-file-export'
                            }
                            onClick={() => {
                                handlePrintPdf()
                            }}
                        />

                        <Button
                            className="p-button-info p-button-sm m-1 w-10rem"
                            icon="pi pi-cog"
                            label={translateKeys('SETTINGS')}
                            onClick={() => obsConfig.current?.showForm()}
                        />
                    </div>
                </div>
                <div className="col-12 flex justify-content-center">
                    {renderRatingScale()}
                </div>
                <div className="col-12 flex justify-content-between align-items-center p-0">
                    <div className="md:col-3 flex justify-content-start">
                        <Button
                            label={translateKeys('Previous')}
                            style={{width: 120}}
                            icon="pi pi-angle-left"
                            disabled={!!obsData?.last}
                            onClick={() =>
                                setPageable({
                                    ...pageable,
                                    pageNumber: pageable.pageNumber + 1,
                                })
                            }
                        />
                    </div>
                    <div className="md:col-3 flex justify-content-end">
                        <Button
                            label={translateKeys('Next')}
                            style={{width: 120}}
                            icon="pi pi-angle-right"
                            iconPos="right"
                            onClick={() =>
                                setPageable({
                                    ...pageable,
                                    pageNumber: pageable.pageNumber - 1,
                                })
                            }
                            disabled={!!obsData?.first}
                        />
                    </div>
                </div>
            </div>
        )
    }

    const renderTableFooter = useCallback(() => {
        return null
    }, [])
    //#endregion

    const location = useLocation()
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search)

        const isOpenForm = queryParams.get('isOpenSymptomForm') === 'true'

        if (isOpenForm && symptomSideBar.current) {
            symptomSideBar.current.showForm()
        }
    }, [location])

    //#region DRAW CHART LINES

    const [reDraw, setReDraw] = useState(0)

    var resizeTimeOut
    var scrollTimeout

    const removeExistingLineCharts = useCallback(() => {
        const lineChartWrapper =
            window.document.getElementById('leader-line-defs')
        if (!!lineChartWrapper) {
            try {
                lineChartWrapper?.remove()
            } catch (error) {}
        }

        if (lineRef.current?.length > 0) {
            lineRef.current.forEach(item => {
                try {
                    item?.remove()
                } catch (error) {}
            })
            lineRef.current = []
            canvasLineRef.current = []
        }
    }, [])

    const drawChart = useCallback((data, key) => {
        if (!data || !key) {
            return
        }

        let chartData = getValueByKeyForChart(data, key)
        if (chartData && chartData.length > 0) {
            const lastIndex = chartData.lastIndexOf(
                chartData[chartData.length - 1],
            )
            const colData = obsColWrapperData.find(x => x.key === key)

            for (let i = 0; i < chartData.length; i++) {
                if (key === observationKeys.bloodPressure) {
                    const systolicIndex = getIndexOfValueInMultiCol(
                        colData.dataRange,
                        chartData[i].systolic,
                    )
                    const diastolicIndex = getIndexOfValueInMultiCol(
                        colData.dataRange,
                        chartData[i].diastolic,
                    )

                    if (systolicIndex !== null && diastolicIndex !== null) {
                        try {
                            const start = `${key}-${chartData[i].systolicIndex}-${systolicIndex}`
                            const end = `${key}-${chartData[i].diastolicIndex}-${diastolicIndex}`

                            const firstLabel =
                                chartData[i].bloodPressurePosition &&
                                chartData[i].bloodPressureLimb !== 'undefined'
                                    ? chartData[i].bloodPressureLimb.substring(
                                          0,
                                          2,
                                      )
                                    : ''
                            const secondLabel =
                                chartData[i].bloodPressurePosition &&
                                chartData[i].bloodPressurePosition !==
                                    'undefined'
                                    ? chartData[
                                          i
                                      ].bloodPressurePosition.substring(0, 2)
                                    : ''

                            if (start && end) {
                                try {
                                    let middleLabel = `${firstLabel || ''}${
                                        firstLabel && secondLabel ? '/' : ''
                                    }${secondLabel || ''}`
                                    const line = new window.LeaderLine(
                                        document.getElementById(start),
                                        document.getElementById(end),
                                        {
                                            size: 2,
                                            // path: 'straight',
                                            color: 'black',
                                            startPlug: 'disc',
                                            endPlug: 'disc',
                                            middleLabel:
                                                window.LeaderLine.captionLabel(
                                                    middleLabel,
                                                    {
                                                        color: 'black',
                                                        fontWeight: 'bold',
                                                        fontSize: 12,
                                                        stroke: 'red',
                                                    },
                                                ),
                                        },
                                    )
                                    let canvasLinesItem = {
                                        fromId: start,
                                        toId: end,
                                        drawVerticalLine: true,
                                        midLabel: middleLabel,
                                    }
                                    canvasLineRef.current.push(canvasLinesItem)
                                    lineRef.current.push(line)
                                } catch (error) {}
                            }
                        } catch (error) {}
                    }
                } else {
                    if (
                        lastIndex !== i &&
                        chartData[i].index === chartData[i + 1].index - 1
                    ) {
                        const dataIndexInCol = getIndexOfValueInMultiCol(
                            colData.dataRange,
                            colData?.key === observationKeys.temperature &&
                                chartData[i]?.device === 'W'
                                ? convertSkinTempToMBT(chartData[i].value)
                                : chartData[i].value,
                        )
                        const dataIndexInColIncreased =
                            getIndexOfValueInMultiCol(
                                colData.dataRange,
                                colData?.key === observationKeys.temperature &&
                                    chartData[i + 1]?.device === 'W'
                                    ? convertSkinTempToMBT(
                                          chartData[i + 1].value,
                                      )
                                    : chartData[i + 1].value,
                            )
                        if (dataIndexInCol !== null) {
                            try {
                                const start = `${key}-${chartData[i].index}-${dataIndexInCol}`
                                const end = `${key}-${
                                    chartData[i + 1].index
                                }-${dataIndexInColIncreased}`

                                if (!!start && !!end) {
                                    try {
                                        const line = new window.LeaderLine(
                                            document.getElementById(start),
                                            document.getElementById(end),
                                            {
                                                size: 2,
                                                path: 'straight',
                                                color: 'black',
                                                startPlug: 'disc',
                                            },
                                        )
                                        let canvasLinesItem = {
                                            fromId: start,
                                            toId: end,
                                        }
                                        canvasLineRef.current.push(
                                            canvasLinesItem,
                                        )
                                        lineRef.current.push(line)
                                    } catch (error) {}
                                }
                            } catch (error) {}
                        }
                    }
                }
            }
        }
    }, [])

    const drawLineCharts = () => {
        removeExistingLineCharts()
        if (data && data?.length > 0) {
            const chartKeys = [
                observationKeys.respiration,
                observationKeys.oxygen,
                observationKeys.bloodPressure,
                observationKeys.heartRate,
                observationKeys.temperature,
            ]

            for (let i = 0; i < chartKeys.length; i++) {
                drawChart(data, chartKeys[i])
            }

            setLineChartDrawn(true)
        }
    }

    const removeLinesIfOverflow = () => {
        const lines = window.document.getElementsByClassName('leader-line')
        if (lines && lines.length > 0) {
            for (var i = lines.length - 1; i >= 0; i--) {
                if (lines[i]) {
                    const informationWrapper =
                        document.getElementsByClassName('basic-info-wrapper')[0]
                    const isHidden =
                        !informationWrapper ||
                        informationWrapper?.style.display === 'none'
                    console.log('isHidden', isHidden)

                    const threshold = !isHidden ? 580 : 55
                    try {
                        if (lines[i]?.style.left) {
                            const left = parseInt(lines[i].style.left)
                            // console.log(left, threshold)

                            if (left < threshold) {
                                try {
                                    lines[i]?.remove()
                                } catch (error) {}

                                continue
                            }
                        }

                        if (lines[i]?.style.right) {
                            const right = parseInt(lines[i].style.right)

                            console.log(right, threshold)
                            if (right < threshold) {
                                try {
                                    lines[i]?.remove()
                                } catch (error) {}

                                continue
                            }
                        }
                    } catch (error) {}
                }
            }
        }
    }

    const handleScrollTable = useCallback(
        e => {
            clearTimeout(scrollTimeout)

            removeExistingLineCharts()
            scrollTimeout = setTimeout(() => {
                drawLineCharts()
                removeLinesIfOverflow()
            }, 100)
        },
        [drawLineCharts, removeExistingLineCharts],
    )

    let redrawTimeout = null

    useEffect(() => {
        obsEvent.on('MENU-CHANGED', () => {
            const detailActiveTabView = localStorage.getItem(
                'detailActiveTabView',
            )

            if (
                !!detailActiveTabView &&
                detailActiveTabView === 'observation'
            ) {
                clearTimeout(redrawTimeout)

                removeExistingLineCharts()

                redrawTimeout = setTimeout(() => {
                    drawLineCharts()
                }, 300)
            } else {
                clearTimeout(redrawTimeout)

                removeOnMenuChanged()

                return
            }
        })

        return () => {
            clearTimeout(redrawTimeout)

            removeOnMenuChanged()
        }
    }, [data])

    useEffect(() => {
        if (!isArrayAndNotEmpty(data)) {
            removeExistingLineCharts()

            return
        }

        if (data && data?.length > 0) {
            drawLineCharts()
        }

        return () => {
            removeExistingLineCharts()
            clearTimeout(scrollTimeout)
        }
    }, [data])

    useEffect(() => {
        window.addEventListener('resize', () => {
            const detailActiveTabView = localStorage.getItem(
                'detailActiveTabView',
            )

            if (detailActiveTabView === 'observation') {
                clearTimeout(resizeTimeOut)

                removeExistingLineCharts()

                resizeTimeOut = setTimeout(() => {
                    drawLineCharts()
                }, 200)
            } else {
                clearTimeout(resizeTimeOut)

                window?.removeEventListener('resize', () => {})

                return
            }
        })

        return () => {
            clearTimeout(resizeTimeOut)

            window?.removeEventListener('resize', () => {})
        }
    }, [data])
    //#endregion

    //#region OBS CHART
    const renderObsChart = () => {
        return (
            <DataTable
                ref={chartWrapper}
                id="obs"
                rowClassName="obs-table-row-chart"
                onScrollCapture={e => handleScrollTable(e)}
                value={obsColWrapperData}
                header={renderTableHeader()}
                footer={renderTableFooter()}
                showGridlines
                responsiveLayout="scroll"
                rowHover
                loading={isLoadingObs}>
                <Column
                    frozen={true}
                    body={rowData => renderColWrapper(rowData, 'head')}
                    style={{
                        backgroundColor: 'white',
                        width: '8%',
                        padding: 0,
                    }}
                />
                {renderColBody()}
            </DataTable>
        )
    }
    //#endregion

    //#region OBS SUMMARY
    const onViewSummary = () => {
        const currentObsDataList = data ? [...data] : []

        obsSummary.current.showForm(currentObsDataList, parameterData)
    }
    //#endregion
    //#endregion

    const renderCanvasLine = () => {
        const canvasLines = canvasLineRef.current || []

        if (
            canvasLines &&
            Array.isArray(canvasLines) &&
            canvasLines.length > 0
        ) {
            return (
                <div>
                    {canvasLines.map((item, idx) => (
                        <LineCanvas
                            key={idx}
                            wrapperId={`canvas-wrapper`}
                            fromId={item.fromId}
                            toId={item.toId}
                            drawVerticalLine={item.drawVerticalLine}
                            midLabel={item.midLabel}
                        />
                    ))}
                </div>
            )
        }
    }

    return (
        <React.Fragment>
            <div className="col-12 p-0 mb-3">
                <div
                    className="flex flex-wrap align-items-center justify-content-between px-3 py-2"
                    style={{
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px',
                    }}>
                    <div className="flex align-items-center py-2 col-12 md:col-9 lg:col-9">
                        <div className="mr-2">
                            <BsRobot height={30} width={30} color="darkcyan" />
                        </div>
                        <div>
                            <span className=" text-sm md:text-md lg:text-lg xl:text-xl font-bold mb-0">
                                {translateCommons("I'm a virtual assistant.")}{' '}
                                To get a summary overview of the latest
                                observations for {` `}
                                <span className="text-blue-500">
                                    {' '}
                                    {userData?.fullName ||
                                        translateKeys('this patient')}
                                </span>
                                . Please click on{' '}
                                <span className="text-darkcyan">
                                    the button to the right{' '}
                                    <span className="mx-1">
                                        <i className="pi pi-arrow-right"></i>
                                    </span>
                                </span>{' '}
                            </span>
                        </div>
                    </div>
                    <div className="col-12 md:col-3 lg:col-2 flex justify-content-end">
                        <ReportButton handleViewSummary={onViewSummary} />
                    </div>
                </div>
            </div>

            {/* Use relative to fix the canvas frame */}
            <div id="canvas-wrapper" ref={screenshotRef} className="relative">
                {/* OBS DATATABLE */}
                <div className="col-12 px-0 obs-table-chart">
                    {renderObsChart()}
                    <div id={'obs-line-leaders'}></div>
                </div>
                <div> {isShowCanvasLine && renderCanvasLine()}</div>
            </div>

            {/* OBS FORM */}
            <ObservationsForm
                ref={obsForm}
                userData={userData}
                obsData={obsData?.content ?? []}
                reload={() => {
                    setPageable({...pageable, pageNumber: 0})
                    refetchObs()
                }}
            />
            <ObservationConfig
                ref={obsConfig}
                reload={() => window.location.reload()}
            />
            <ObservationPdfPreview
                ref={obsPdf}
                pageable={initialPageable}
                fileName={`PatientObs`}
            />
            <ObservationComment ref={obsComment} />
            <ObservationSummary ref={obsSummary} currentObsDataList={data} />
            <StyledNoteSideBar ref={noteSideBar} />
            <StyledSymptomSideBar ref={symptomSideBar} />
            <StyledParameterSideBar ref={parameterSideBar} />
        </React.Fragment>
    )
}
export default Observations
